import { FLAGS, WISEPOPS_REVENUE_GOAL_ID, WISEPOPS_SITE_ID } from "@/define";
import { logEvent, logException } from "./logger";
import { getCookie } from "./helper";
import { useBuyFlowStore } from "@/stores/buyFlow";
import { googleTagCartEvents } from "./googleTagEvents";
import { ref } from "vue";
import { OneTrustGroups } from "@/globalEnums";

// dictionary to keep track if the click listener with certain popup id has been registered
const registeredPopupListeners = ref<Map<string, boolean>>(new Map());

// is required to avoid TypeScript build errors as script is loaded during runtime
declare global {
  interface IWisepopsFunction {
    (action: string, ...args: unknown[]): void;
    q?: unknown[];
    l?: number;
    _nonce?: string;
  }
  interface OneTrustAPI {
    IsAlertBoxClosedAndValid(): boolean;
  }

  interface Window {
    OnetrustActiveGroups: string;
  }

  var wisepops: IWisepopsFunction;
  var OneTrust: OneTrustAPI;
}

// Initialize Wisepops only if the feature flag is enabled and we are on the correct domain
export async function initWisepops() {
  if (FLAGS.ENABLE_WISEPOPS !== "true") {
    return;
  }

  const key = WISEPOPS_SITE_ID;
  if (!key) {
    logEvent("Invalid domain for Wisepops");
    return;
  }

  window["wisepops"] =
    window["wisepops"] ||
    function (...args) {
      (window["wisepops"].q = window["wisepops"].q || []).push(args);
    };
  window["wisepops"].l = Date.now();
  window["wisepops"]._nonce = document.currentScript?.nonce;
  const wisepopsScript = document.createElement("script") as HTMLScriptElement;
  const firstScript = document.getElementsByTagName("script")[0];
  wisepopsScript.nonce = document.currentScript?.nonce || "";
  wisepopsScript.defer = true;
  wisepopsScript.classList = "optanon-category-C0004";
  wisepopsScript.src = "https://wisepops.net/loader.js?v=3&h=" + key;
  if (firstScript.parentNode != null) {
    firstScript.parentNode.insertBefore(wisepopsScript, firstScript);
  }
  logEvent("Wisepops script initialized");
}

// check for onetrust consent before loading wisepops
export async function canLoadWisepops(): Promise<boolean> {
  return new Promise(resolve => {
    const checkConsent = () => {
      try {
        if (FLAGS.ADD_ONETRUST_CLASS_TO_WISEPOPS === "true") {
          // if one trust is active, then check if cookie preferences are set
          // if set only then resolve true
          if (OneTrust.IsAlertBoxClosedAndValid()) {
            if (window.OnetrustActiveGroups.includes(OneTrustGroups.C0004.toString())) {
              console.log("true");
              resolve(true);
              return;
            } else {
              console.log("false");
              resolve(false);
              return;
            }
          } else {
            console.log("timeout");
            setTimeout(checkConsent, 100);
            return;
          }
          // if onetrust is not active
        } else {
          resolve(true);
          return;
        }
      } catch (error) {
        // Ignore error - expected while OneTrust is loading
        console.log("timeout error", error);
        setTimeout(checkConsent, 100);
      }
    };
    checkConsent();
  });
}

// Function to wait until the wisepops script is loaded
async function waitForWisepops(): Promise<void> {
  return new Promise(resolve => {
    const checkWisepops = () => {
      try {
        wisepops("log");
        resolve();
      } catch {
        setTimeout(checkWisepops, 100);
      }
    };
    checkWisepops();
  });
}

export async function addWisepopsEventListener() {
  if (FLAGS.ENABLE_WISEPOPS !== "true") {
    return;
  }
  await waitForWisepops();
  // this is the listener for the popup display, the code inside will run each time the popup is displayed
  // we create a cookie on each popup display with the popup id on wisepops side,
  // so we can use it to register the click listener only once per popup
  wisepops("listen", "before-popup-display", async function () {
    await new Promise(res => setTimeout(res, 100));
    const popupId = getCookie("popup_id");
    if (popupId && !registeredPopupListeners.value.get(popupId)) {
      // this listener will run each time a user clicks on a tracked button inside the popup with the given popup id
      wisepops("listen", "after-tracked-click", Number(popupId), event => handlePromoCodeFromClick(event, popupId));
      // set the flag to true to avoid registering the click listener again
      registeredPopupListeners.value.set(popupId, true);
    }
  });
}

// to handle promo code application from wisepops click events
async function handlePromoCodeFromClick(event: unknown, popupId: string): Promise<void> {
  await new Promise(res => setTimeout(res, 100));
  // the promo code is set in a cookie on the wisepops side, on click of the tracked button
  const promoCode = getCookie(popupId + "_promo");
  if (promoCode) {
    logEvent(`Load promo code ${promoCode}`, "wisepops.ts", JSON.stringify(event));
    await addPromoCode(promoCode);
  }
}

// to add promo code to the cart and update the total
export async function addPromoCode(promoCode: string) {
  const buyFlowStore = useBuyFlowStore();
  if (buyFlowStore.cart && promoCode) {
    buyFlowStore.cart.promotionCode = promoCode;
  }
  try {
    await buyFlowStore.updateShoppingCartTotal(false, true, googleTagCartEvents.change);
  } catch (error) {
    if (buyFlowStore.cart) {
      buyFlowStore.cart.promotionCode = "";
    }
    logException(error as Error, `addPromoCode: Invalid promo code ${promoCode}`);
  }
}

export async function hideAllWisepopsPopups() {
  wisepops("listen", "before-popup-display", function (event) {
    event.preventDefault();
  });
}

export async function setWisepopsRevenue(revenue: number) {
  wisepops("goal", WISEPOPS_REVENUE_GOAL_ID, {
    revenue: revenue,
  });
}
